import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Trash2, Copy, Check, KeyRound, AlertTriangle } from 'lucide-react';
import { apiKeysApi, type ApiKey } from '../../api/apiKeys';
import EmptyState from '../EmptyState';
import { useConfirm } from '../ConfirmDialog';

function fmtDate(d: string | null) {
  return d ? new Date(d).toLocaleDateString() : '—';
}

const SCOPE_OPTIONS: { value: string; label: string; hint: string }[] = [
  { value: 'tickets:create', label: 'Create tickets', hint: 'File new tickets into this company' },
  { value: 'tickets:read', label: 'Read tickets', hint: 'List and fetch tickets + messages' },
  { value: 'tickets:write', label: 'Reply to tickets', hint: 'Post replies / internal notes back to tickets' },
  { value: 'ai:dispatch', label: 'AI dispatch', hint: 'Claude dispatch agent: claim Send-to-Claude jobs and download their attachments' },
  { value: 'crm:leads', label: 'Post CRM leads', hint: 'Contact forms: drop submissions into the CRM inbox (POST /api/v1/crm/leads)' },
];

export default function ApiKeysTab() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [newName, setNewName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['tickets:create', 'tickets:read']);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const toggleScope = (value: string) =>
    setScopes((prev) => (prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]));

  const { data: keys = [], isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => apiKeysApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => apiKeysApi.create({ name, scopes }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      setCreatedSecret(res.key);
      setNewName('');
      setScopes(['tickets:create', 'tickets:read']);
      toast.success('API key created — copy it now, it won\'t be shown again');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success('Key revoked');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Permissions are editable in place: an integration that turns out to need
  // one more scope shouldn't force a secret rotation through the other app.
  const scopesMutation = useMutation({
    mutationFn: ({ id, scopes }: { id: string; scopes: string[] }) => apiKeysApi.updateScopes(id, scopes),
    onSuccess: (key) => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success(`Permissions updated for ${key.name}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleKeyScope = (k: ApiKey, scope: string) => {
    const current = k.scopes || [];
    const next = current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope];
    if (next.length === 0) {
      toast.error('A key needs at least one permission — revoke it instead');
      return;
    }
    scopesMutation.mutate({ id: k.id, scopes: next });
  };

  const copySecret = () => {
    if (!createdSecret) return;
    navigator.clipboard.writeText(createdSecret);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const isActive = (k: ApiKey) => !k.revokedAt && (!k.expiresAt || new Date(k.expiresAt) > new Date());

  const handleRevoke = async (k: ApiKey) => {
    if (await confirm({
      title: 'Revoke API key',
      message: `Revoke "${k.name}"? The app will stop being able to file tickets.`,
      confirmLabel: 'Revoke',
      danger: true,
    })) {
      revokeMutation.mutate(k.id);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="section-title">API Keys (Integrations)</h2>
        <p className="text-sm text-muted mt-1">
          Issue a key per application (e.g. ClavaNet, ctAgencySuite) so it can file tickets into this
          company via the inbound API. The key identifies both the app and the company.
        </p>
      </div>

      {/* One-time secret reveal */}
      {createdSecret && (
        <div className="card p-4 border border-warn">
          <div className="flex items-start gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-warn mt-0.5 shrink-0" />
            <p className="text-sm text-warn">
              Copy this key now — it will <strong>not</strong> be shown again. Store it as a secret in the calling app.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 rounded-lg bg-row-odd text-xs font-mono break-all text-fg">{createdSecret}</code>
            <button className="btn btn-secondary text-sm" onClick={copySecret}>
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button className="btn btn-ghost text-sm mt-2" onClick={() => setCreatedSecret(null)}>Done</button>
        </div>
      )}

      {/* Create */}
      <div className="card p-4 space-y-3">
        <div>
          <label className="label">Authorize a new app</label>
          <div className="flex gap-2">
            <input
              className="input flex-1"
              placeholder="App name (e.g. clavanet-prod)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && newName.trim() && scopes.length > 0 && createMutation.mutate(newName.trim())}
            />
            <button
              className="btn btn-primary"
              disabled={!newName.trim() || scopes.length === 0 || createMutation.isPending}
              onClick={() => createMutation.mutate(newName.trim())}
            >
              <Plus className="w-4 h-4" /> Create Key
            </button>
          </div>
        </div>
        <div>
          <span className="label">Permissions</span>
          <div className="flex flex-wrap gap-2 mt-1">
            {SCOPE_OPTIONS.map((s) => {
              const on = scopes.includes(s.value);
              return (
                <button
                  key={s.value}
                  type="button"
                  title={s.hint}
                  onClick={() => toggleScope(s.value)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                    on
                      ? 'bg-primary-muted border-primary text-primary'
                      : 'border-line text-muted hover:border-line'
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted mt-1">
            Grant only what the app needs. "Reply to tickets" lets a linked agency post updates back into a thread.
          </p>
        </div>
      </div>

      {/* List */}
      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="p-6 text-center text-muted">Loading…</div>
        ) : keys.length === 0 ? (
          <EmptyState icon={<KeyRound className="w-6 h-6" />}>
            No API keys yet. Click <strong>Create Key</strong> above to let an app file tickets here.
          </EmptyState>
        ) : (
          <table className="scpa-table">
            <thead>
              <tr>
                <th className="scpa-th">App</th>
                <th className="scpa-th">Key prefix</th>
                <th className="scpa-th">Scopes</th>
                <th className="scpa-th">Last used</th>
                <th className="scpa-th">Status</th>
                <th className="scpa-th"></th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} className="scpa-row" style={{ cursor: 'default' }}>
                  <td className="scpa-td font-medium text-fg">{k.name}</td>
                  <td className="scpa-td font-mono text-xs text-muted">ctk_live_{k.keyPrefix}…</td>
                  <td className="scpa-td">
                    {isActive(k) ? (
                      <div className="flex flex-wrap gap-1">
                        {SCOPE_OPTIONS.map((s) => {
                          const on = (k.scopes || []).includes(s.value);
                          return (
                            <button
                              key={s.value}
                              type="button"
                              title={`${s.hint} — click to ${on ? 'remove' : 'grant'}`}
                              disabled={scopesMutation.isPending}
                              onClick={() => toggleKeyScope(k, s.value)}
                              className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors ${
                                on
                                  ? 'bg-primary-muted border-primary text-primary'
                                  : 'border-line text-muted hover:text-fg'
                              }`}
                            >
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-xs text-muted">{(k.scopes || []).join(', ')}</span>
                    )}
                  </td>
                  <td className="scpa-td text-muted">{fmtDate(k.lastUsedAt)}</td>
                  <td className="scpa-td">
                    {isActive(k) ? (
                      <span className="badge text-ok">Active</span>
                    ) : (
                      <span className="badge text-muted">Revoked</span>
                    )}
                  </td>
                  <td className="scpa-td text-right">
                    {isActive(k) && (
                      <button
                        className="btn btn-ghost text-sm text-err"
                        onClick={() => handleRevoke(k)}
                      >
                        <Trash2 className="w-4 h-4" /> Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-xs text-muted space-y-1">
        <p>Apps call <code className="font-mono">POST /api/v1/ingest/tickets</code> with header <code className="font-mono">Authorization: Bearer ctk_live_…</code>.</p>
        <p>See the integration guide (docs/integration-api.md) for the payload and examples.</p>
      </div>
    </div>
  );
}
